/**
 * Surprav Wishlist — server-only, login-gated.
 *
 * There is NO local/guest wishlist. The wishlist IS the logged-in customer's
 * metafield, read/written through the Shopify App Proxy (/apps/surprav/wishlist).
 *
 *  - Logged out: clicking a heart redirects to login (the product is added
 *    automatically once they return, via a return_to flag — no localStorage).
 *  - Logged in: hearts reflect the server wishlist; clicking adds/removes on the
 *    server with optimistic UI (and reverts on failure). Remove is authoritative,
 *    so removed items stay removed (no merge resurrection).
 *
 * Public API (window.SurpravWishlist), used by the wishlist page:
 *   reload() -> Promise   refresh from the server
 *   ids()                 array of productId strings currently saved
 *   has(id) / count()
 *   remove(id) -> Promise DELETE on the server + update UI
 */
(function () {
  'use strict';

  var PROXY = '/apps/surprav/wishlist';
  var BTN_SELECTOR =
    '[data-wishlist-btn][data-product-id],' +
    '.deals-row__wishlist[data-product-id],' +
    '.product-card__wishlist[data-product-id]';

  var ids = null;         // Set<string> — server state, null until first load
  var loadPromise = null; // de-dupe concurrent loads

  function cfg() { return window.__surprav || {}; }
  function isLoggedIn() { return !!cfg().customerId; }
  function pidOf(v) { return String(v == null ? '' : v).trim(); }

  function emit(name, data) {
    if (typeof publish === 'function') publish(name, data);
    else if (window.Surprav && Surprav.PubSub && typeof Surprav.PubSub.publish === 'function') {
      Surprav.PubSub.publish(name, data);
    }
  }

  /** Redirect a logged-out shopper to login, remembering the intended add. */
  function loginRedirect(pendingId) {
    var path = window.location.pathname + window.location.search;
    if (pendingId) {
      var sep = path.indexOf('?') > -1 ? '&' : '?';
      path = path + sep + 'wishlist_add=' + encodeURIComponent(pendingId);
    }
    window.location.href = '/customer_authentication/login?return_to=' + encodeURIComponent(path);
  }

  // ── Server I/O ───────────────────────────────────────────
  function loadServer() {
    if (!isLoggedIn()) { ids = new Set(); return Promise.resolve(ids); }
    if (loadPromise) return loadPromise;
    loadPromise = fetch(PROXY, { method: 'GET', headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (res) { return res.ok ? res.json() : { items: [] }; })
      .then(function (data) {
        ids = new Set((data.items || []).map(function (it) { return pidOf(it.productId); }).filter(Boolean));
      })
      .catch(function () { if (!ids) ids = new Set(); })
      .then(function () { loadPromise = null; return ids; });
    return loadPromise;
  }

  function addRemote(pid) {
    return fetch(PROXY, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [{ productId: pid, addedAt: new Date().toISOString() }] })
    });
  }

  function removeRemote(pid) {
    return fetch(PROXY, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ productId: pid })
    });
  }

  // ── UI ───────────────────────────────────────────────────
  function applyState(node, active) {
    node.classList.toggle('is-active', active);
    node.setAttribute('aria-pressed', active ? 'true' : 'false');
    node.setAttribute('aria-label', active ? 'Verwijderen uit wishlist' : 'Toevoegen aan wishlist');
  }

  function setButtonState(pid, active) {
    var nodes = document.querySelectorAll(
      '[data-wishlist-btn][data-product-id="' + pid + '"],' +
      '.deals-row__wishlist[data-product-id="' + pid + '"],' +
      '.product-card__wishlist[data-product-id="' + pid + '"]'
    );
    for (var i = 0; i < nodes.length; i++) applyState(nodes[i], active);
  }

  function syncAllButtons() {
    var nodes = document.querySelectorAll(BTN_SELECTOR);
    for (var i = 0; i < nodes.length; i++) {
      applyState(nodes[i], !!ids && ids.has(pidOf(nodes[i].getAttribute('data-product-id'))));
    }
  }

  function syncCount() {
    var n = ids ? ids.size : 0;
    var badges = document.querySelectorAll('[data-wishlist-count]');
    for (var i = 0; i < badges.length; i++) {
      badges[i].textContent = n > 99 ? '99+' : String(n);
      if (n > 0) badges[i].removeAttribute('hidden'); else badges[i].setAttribute('hidden', '');
    }
  }

  // ── Toggle on heart click ────────────────────────────────
  function toggle(pid) {
    pid = pidOf(pid);
    if (!ids || !pid) return Promise.resolve(false);
    var wasActive = ids.has(pid);
    if (wasActive) ids.delete(pid); else ids.add(pid);  // optimistic
    setButtonState(pid, !wasActive);
    syncCount();
    return (wasActive ? removeRemote(pid) : addRemote(pid))
      .then(function (res) {
        if (!res.ok) throw new Error('http_' + res.status);
        emit('wishlist:change', { action: wasActive ? 'remove' : 'add', productId: pid });
        return !wasActive;
      })
      .catch(function () {
        if (wasActive) ids.add(pid); else ids.delete(pid); // revert
        setButtonState(pid, wasActive);
        syncCount();
        return wasActive;
      });
  }

  function onButtonClick(event) {
    var btn = event.currentTarget;
    var pid = btn.getAttribute('data-product-id');
    if (!pid) return;
    if (event.preventDefault) event.preventDefault();
    if (!isLoggedIn()) { loginRedirect(pid); return; }
    if (!ids) { loadServer().then(function () { toggle(pid); }); return; }
    toggle(pid);
  }

  // ── Public API ───────────────────────────────────────────
  var SurpravWishlist = {
    isLoggedIn: isLoggedIn,
    reload: loadServer,
    ids: function () { return ids ? Array.prototype.slice.call(ids) : []; },
    has: function (pid) { return !!ids && ids.has(pidOf(pid)); },
    count: function () { return ids ? ids.size : 0; },
    remove: function (pid) {
      pid = pidOf(pid);
      if (!ids || !ids.has(pid)) return Promise.resolve(false);
      ids.delete(pid); // optimistic
      setButtonState(pid, false);
      syncCount();
      return removeRemote(pid)
        .then(function (res) {
          if (!res.ok) throw new Error('http');
          emit('wishlist:change', { action: 'remove', productId: pid });
          return true;
        })
        .catch(function () { ids.add(pid); setButtonState(pid, true); syncCount(); return false; });
    },
    _syncAllButtons: syncAllButtons,
    _syncCount: syncCount
  };
  window.SurpravWishlist = SurpravWishlist;

  // ── Init ─────────────────────────────────────────────────
  function bind() {
    var nodes = document.querySelectorAll(BTN_SELECTOR);
    for (var i = 0; i < nodes.length; i++) nodes[i].addEventListener('click', onButtonClick);
  }

  function honorPendingAdd() {
    var m = /[?&]wishlist_add=([^&]+)/.exec(window.location.search);
    if (!m) return;
    var pid = pidOf(decodeURIComponent(m[1]));
    // strip the flag from the URL so a refresh doesn't re-add
    try {
      var url = new URL(window.location.href);
      url.searchParams.delete('wishlist_add');
      window.history.replaceState({}, '', url.pathname + (url.search || '') + url.hash);
    } catch (_e) { /* older browsers — leave it */ }
    if (pid && ids && !ids.has(pid)) {
      ids.add(pid);
      setButtonState(pid, true);
      addRemote(pid).then(function () { emit('wishlist:change', { action: 'add', productId: pid }); });
    }
  }

  function init() {
    bind();
    if (isLoggedIn()) {
      loadServer().then(function () {
        honorPendingAdd();
        syncAllButtons();
        syncCount();
      });
    } else {
      ids = new Set(); // logged out → nothing saved; hearts inactive
      syncCount();
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
